import { useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'

export function usePagination(data = [], itemsPerPage = 10) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const currentPage = Number(searchParams.get('page')) || 1

  const totalItems = data.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  const paginatedData = useMemo(() => {
    return data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
  }, [data, currentPage, itemsPerPage])

  const navigateToPage = (page) => {
    searchParams.set('page', page)
    navigate(`?${searchParams.toString()}`)
  }

  const hasNextPage = currentPage < totalPages
  const hasPreviousPage = currentPage > 1

  // SMART PAGINATION: Generate grouped page numbers to prevent overflow
  const getSmartPageNumbers = () => {
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const delta = 2 // Pages to show around current page
    const range = []
    const rangeWithDots = []

    // Always include first page
    range.push(1)

    // Calculate start and end of middle range
    const start = Math.max(2, currentPage - delta)
    const end = Math.min(totalPages - 1, currentPage + delta)

    // Add middle range
    for (let i = start; i <= end; i++) {
      range.push(i)
    }

    // Always include last page
    if (totalPages > 1) {
      range.push(totalPages)
    }

    // Remove duplicates and sort
    const uniqueRange = [...new Set(range)].sort((a, b) => a - b)

    // Add dots where there are gaps
    for (let i = 0; i < uniqueRange.length; i++) {
      rangeWithDots.push(uniqueRange[i])
      
      if (uniqueRange[i + 1] && uniqueRange[i + 1] - uniqueRange[i] > 1) {
        rangeWithDots.push('...')
      }
    }

    return rangeWithDots
  }

  const pageNumbers = getSmartPageNumbers()

  return {
    paginatedData,
    currentPage,
    totalPages,
    totalItems,
    pageNumbers,
    hasNextPage,
    hasPreviousPage,
    navigateToPage
  }
}
